/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useEffect, useState } from 'react';

import { EmbeddedSsoPopupLogin, IframeBlockerPage, getEmbeddedSsoMode } from '@coursera-web/embedded-experience';
import type { EmbeddedSsoMode } from '@coursera-web/embedded-experience';
import URI from 'jsuri';

import API from 'js/lib/api';
import * as redirect from 'js/lib/coursera.redirect';

import { LoadingSection } from '@coursera/cds-core';

import _t from 'i18n!nls/enterprise-legacy-learner-home';

type OuterProps = {
  thirdPartyOrganizationId: string;
};

const styles = {
  container: css`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: var(--cds-color-neutral-background-primary);
    z-index: 11000;
    display: grid;
    place-items: center;
  `,
};

const getReturnTo = () =>
  redirect.getPathname() + new URI(redirect.getQueryParams()).deleteQueryParam('attemptSSOLogin').toString();

function BypassLogin({ thirdPartyOrganizationId }: OuterProps) {
  const [embeddedSsoMode, setEmbeddedSsoMode] = useState<EmbeddedSsoMode | null>(null);

  useEffect(() => {
    // A framed C4B launch can't be redirected to the IDP (IDP pages refuse to be framed).
    const mode = getEmbeddedSsoMode();
    if (mode) {
      setEmbeddedSsoMode(mode);
      return;
    }

    const uri = new URI()
      .addQueryParam('action', 'initiateLoginFlow')
      .addQueryParam('id', thirdPartyOrganizationId)
      .addQueryParam('returnTo', getReturnTo());

    API('/api/thirdPartyOrganizations.v1')
      .post(uri.toString())
      .then((res: string) => {
        window.location.href = res;
      })
      .catch(() => {
        const url = new URL(window.location.href);
        url.searchParams.delete('attemptSSOLogin');
        window.location.href = url.toString();
      });
  }, [thirdPartyOrganizationId]);

  if (embeddedSsoMode === 'popup') {
    return (
      <div css={styles.container}>
        <EmbeddedSsoPopupLogin thirdPartyOrganizationId={thirdPartyOrganizationId} returnTo={getReturnTo()} />
      </div>
    );
  }

  if (embeddedSsoMode === 'newTab') {
    return (
      <div css={styles.container}>
        <IframeBlockerPage variant="ssoFallback" continueHref={window.location.href} />
      </div>
    );
  }

  return (
    <div css={styles.container}>
      <LoadingSection label={_t('Redirecting you to your SSO provider...')} />
    </div>
  );
}

export default BypassLogin;
